import { Component, OnDestroy, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { filter, Subscription } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/user.service';
import '@shui/core/sh-spinner';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './callback.component.html',
  styleUrl: './callback.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CallbackComponent implements OnInit, OnDestroy {
  hasError = false;
  errorMessage = '';
  isProcessing = true;
  private hasVerified = false; // Prevent multiple verification attempts

  private loginFailureSubscription: Subscription | undefined;

  constructor(
    private broadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private readonly router: Router,
    private authService: AuthService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.handleAuthenticationCallback();
  }

  handleAuthenticationCallback(): void {
    // Handle the redirect promise first
    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult | null) => {
        if (result) {
          // Set the active account immediately
          this.msalService.instance.setActiveAccount(result.account);
          
          // Store ID token
          if (result.idToken) {
            localStorage.setItem('idToken', result.idToken);
          }
          
          // Verify and navigate immediately after setting account
          this.verifyAndNavigate();
        } else {
          // Check if already authenticated
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            this.verifyAndNavigate();
          } else {
            this.isProcessing = false;
            this.router.navigate(['/login']);
          }
        }
      },
      error: (error) => {
        this.hasError = true;
        this.isProcessing = false;
        this.errorMessage = 'Authentication error. Redirecting to login...';
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      }
    });

    // Subscribe to login failure events as backup
    this.loginFailureSubscription = this.broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE)
      )
      .subscribe((error) => {
        this.hasError = true;
        this.isProcessing = false;
        this.errorMessage = 'Authentication failed. Redirecting to login...';
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      });
  }

  async verifyAndNavigate(): Promise<void> {
    // Prevent multiple verification attempts
    if (this.hasVerified) {
      return;
    }
    
    this.hasVerified = true;
    
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length === 0) {
      this.hasError = true;
      this.hasVerified = false; // Reset for retry
      this.errorMessage = 'Authentication failed. No account found.';
      this.isProcessing = false;
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 2000);
      return;
    }

    const account = accounts[0];
    const userEmail = account.username;
    
    try {
      // Mock user verification
      const verificationResponse = await this.userService.verifyUser(userEmail);
      
      if (verificationResponse.user_exists) {
        this.isProcessing = false;
        
        // Navigate to home page
        await this.router.navigate(['/home']);
      } else {
        this.hasError = true;
        this.hasVerified = false; // Reset for retry
        this.errorMessage = 'Access denied. User not authorized.';
        this.isProcessing = false;
        
        // Logout and redirect to login
        setTimeout(() => {
          this.authService.logout();
        }, 2000);
      }
    } catch (error) {
      this.hasError = true;
      this.hasVerified = false; // Reset for retry
      this.errorMessage = 'Unable to verify user access.';
      this.isProcessing = false;
      
      // Logout and redirect to login
      setTimeout(() => {
        this.authService.logout();
      }, 2000);
    }
  }

  ngOnDestroy(): void {
    this.loginFailureSubscription?.unsubscribe();
  }
}
